/* eslint-disable no-template-curly-in-string */
import { camelCase, template } from 'lodash';

import { __t, applyTranslations } from 'utils/language';
import { insertIf } from 'utils/list';
import {
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_ZALANDO_OUTFIT
} from 'utils/deliverables';
import { SENTIMENT_CLASSES } from 'utils/sentiment';

import type { Campaign } from 'types';
import type { ReportingMetaInfo } from 'modules/reporting/types/ReportingMetaInfo.type';
import type { SideNavigationItem } from 'components/side-navigation-v2/SideNavigation.type';

applyTranslations(require('./reporting-navigation.i18n.json'));

const SUMMARY: SideNavigationItem = {
  type: 'item',
  title: __t('reporting_navigation.summary'),
  trackingKey: 'campaign-report-summary',
  url: '/campaigns/${id}/report/v2',
  includes: [
    '/campaigns/[0-9]+.*/report/v2$'
  ],
  isAvailable: true,
  customUnavailabilityTooltip: __t('reporting_navigation.coming_soon_tooltip')
};

const IG_POSTS: SideNavigationItem = {
  type: 'item',
  title: __t('reporting_navigation.instagram_posts'),
  trackingKey: 'campaign-report-instagram-posts',
  url: '/campaigns/${id}/report/v2/ig-post',
  includes: [
    '/campaigns/[0-9]+.*/report/v2/ig-post'
  ],
  isAvailable: true,
  customUnavailabilityTooltip: __t('reporting_navigation.instagram_post_not_available')
};

const IG_STORIES: SideNavigationItem = {
  type: 'item',
  title: __t('reporting_navigation.instagram_stories'),
  trackingKey: 'campaign-report-instagram-stories',
  url: '/campaigns/${id}/report/v2/ig-stories',
  includes: [
    '/campaigns/[0-9]+.*/report/v2/ig-stories'
  ],
  isAvailable: true,
  customUnavailabilityTooltip: __t('reporting_navigation.instagram_story_not_available')
};

const IG_REELS: SideNavigationItem = {
  type: 'item',
  title: __t('reporting_navigation.instagram_reels'),
  trackingKey: 'campaign-report-instagram-reels',
  url: '/campaigns/${id}/report/v2/ig-reels',
  includes: [
    '/campaigns/[0-9]+.*/report/v2/ig-reels'
  ],
  isAvailable: true,
  customUnavailabilityTooltip: __t('reporting_navigation.instagram_reel_not_available')
};

const TIKTOK: SideNavigationItem = {
  type: 'item',
  title: __t('reporting_navigation.tiktok_videos'),
  trackingKey: 'campaign-report-tiktok',
  url: '/campaigns/${id}/report/v2/tiktok',
  includes: [
    '/campaigns/[0-9]+.*/report/v2/tiktok'
  ],
  isAvailable: true,
  customUnavailabilityTooltip: __t('reporting_navigation.tiktok_video_not_available')
};

const CREATOR_PERFORMANCE: SideNavigationItem = {
  type: 'item',
  title: __t('reporting_navigation.creator_performance'),
  trackingKey: 'campaign-report-creator-performance',
  url: '/campaigns/${id}/report/v2/creators',
  includes: [
    '/campaigns/[0-9]+.*/report/v2/creators'
  ],
  isAvailable: true,
  customUnavailabilityTooltip: __t('reporting_navigation.creator_performance_not_available')
};

const getOnsitePerformanceNavLink = (
  isZalandoOutfitOnly: boolean
): SideNavigationItem => ({
  type: 'item',
  title: __t('reporting_navigation.onsite_performance'),
  trackingKey: 'campaign-report-onsite-performance',
  url: '/campaigns/${id}/report/v2/onsite-performance',
  includes: [
    '/campaigns/[0-9]+.*/report/v2/onsite-performance'
  ],
  isAvailable: true,
  customUnavailabilityTooltip: isZalandoOutfitOnly
    ? __t('reporting_navigation.zalando_outfit_only')
    : __t('reporting_navigation.onsite_performance_not_available')
});

const getCostAttributionNavLink = (
  isZalandoOutfitOnly: boolean
): SideNavigationItem => ({
  type: 'item',
  title: __t('reporting_navigation.cost_attribution'),
  trackingKey: 'campaign-report-cca',
  url: '/campaigns/${id}/report/v2/cca',
  includes: [
    '/campaigns/[0-9]+.*/report/v2/cca'
  ],
  isAvailable: true,
  customUnavailabilityTooltip: isZalandoOutfitOnly
    ? __t('reporting_navigation.zalando_outfit_only')
    : __t('reporting_navigation.cca_not_available')
});

const getContentMetricsNavLink = (
  isZalandoOutfitOnly: boolean
): SideNavigationItem => ({
  type: 'item',
  title: __t('reporting_navigation.content_metrics'),
  trackingKey: 'campaign-report-content-metrics',
  url: '/campaigns/${id}/report/v2/content-metrics',
  includes: [
    '/campaigns/[0-9]+.*/report/v2/content-metrics'
  ],
  isAvailable: true,
  customUnavailabilityTooltip: isZalandoOutfitOnly
    ? __t('reporting_navigation.zalando_outfit_only')
    : __t('reporting_navigation.content_metrics_not_available')
});

const getSentimentAnalysisNavLink = (
  isZalandoOutfitOnly: boolean
): SideNavigationItem => ({
  type: 'item',
  title: __t('reporting_navigation.sentiment_analysis'),
  trackingKey: 'campaign-report-sentiment-analysis',
  url: `/campaigns/\${id}/report/v2/sentiment-analysis?sentimentClass=${SENTIMENT_CLASSES.join(',')}`,
  includes: [
    '/campaigns/[0-9]+.*/report/v2/sentiment-analysis'
  ],
  isAvailable: true,
  customUnavailabilityTooltip: isZalandoOutfitOnly
    ? __t('reporting_navigation.zalando_outfit_only')
    : __t('reporting_navigation.sentiment_analysis_not_available')
});

const getDownloadNavLink = (
  isZalandoOutfitOnly: boolean
): SideNavigationItem => ({
  type: 'item',
  title: __t('reporting_navigation.download'),
  trackingKey: 'campaign-report-download',
  url: '/campaigns/${id}/report/v2/download',
  includes: [
    '/campaigns/[0-9]+.*/report/v2/download'
  ],
  isAvailable: true,
  customUnavailabilityTooltip: isZalandoOutfitOnly
    ? __t('reporting_navigation.zalando_outfit_only')
    : __t('reporting_navigation.download_not_available')
});

const hasDeliverableContent = (metaInfo: ReportingMetaInfo): boolean => {
  if (!metaInfo.content || typeof metaInfo.content !== 'object') {
    return false;
  }

  return Boolean(Object.keys(metaInfo.content)
    .filter(deliverableType => metaInfo.content[deliverableType]).length);
};

export function prepareNavigationItems(campaign: Campaign, navItems: Array<SideNavigationItem>): Array<SideNavigationItem> {
  return navItems.map(item => ({
    ...item,
    url: template(item.url)({
      id: campaign.id
    })
  }));
}

export function getReporting(metaInfo: ReportingMetaInfo): Array<SideNavigationItem> {
  const hasContent = hasDeliverableContent(metaInfo);
  const hasIGPosts = metaInfo.campaignDeliverableTypes.includes(DELIVERABLE_TYPE_INSTAGRAM_POST);
  const hasIGStories = metaInfo.campaignDeliverableTypes.includes(DELIVERABLE_TYPE_INSTAGRAM_STORY);
  const hasIGReels = metaInfo.campaignDeliverableTypes.includes(DELIVERABLE_TYPE_INSTAGRAM_REEL);
  const hasTikTok = metaInfo.campaignDeliverableTypes.includes(DELIVERABLE_TYPE_TIKTOK_VIDEO);
  const isZalandoOutfitOnly = metaInfo.campaignDeliverableTypes
    .every(type => type === DELIVERABLE_TYPE_ZALANDO_OUTFIT);

  const onsitePerformance = getOnsitePerformanceNavLink(isZalandoOutfitOnly);

  return [
    SUMMARY,
    ...insertIf(hasIGPosts, {
      ...IG_POSTS,
      isAvailable: IG_POSTS.isAvailable && metaInfo.content[camelCase(DELIVERABLE_TYPE_INSTAGRAM_POST)]
    }),
    ...insertIf(hasIGStories, {
      ...IG_STORIES,
      isAvailable: IG_STORIES.isAvailable && metaInfo.content[camelCase(DELIVERABLE_TYPE_INSTAGRAM_STORY)]
    }),
    ...insertIf(hasIGReels, {
      ...IG_REELS,
      isAvailable: IG_REELS.isAvailable && metaInfo.content[camelCase(DELIVERABLE_TYPE_INSTAGRAM_REEL)]
    }),
    ...insertIf(hasTikTok, {
      ...TIKTOK,
      isAvailable: TIKTOK.isAvailable && metaInfo.content[camelCase(DELIVERABLE_TYPE_TIKTOK_VIDEO)]
    }),
    {
      ...CREATOR_PERFORMANCE,
      isAvailable: CREATOR_PERFORMANCE.isAvailable && hasContent
    },
    {
      ...onsitePerformance,
      isAvailable: onsitePerformance.isAvailable && metaInfo.hasWmcLinks
    }
  ];
}

export function getActions(metaInfo: ReportingMetaInfo): Array<SideNavigationItem> {
  const hasContent = hasDeliverableContent(metaInfo);
  const isZalandoOutfitOnly = metaInfo.campaignDeliverableTypes
    .every(type => type === DELIVERABLE_TYPE_ZALANDO_OUTFIT);

  return [
    {
      ...getCostAttributionNavLink(isZalandoOutfitOnly),
      isAvailable: metaInfo.hasAgreedOffers && !isZalandoOutfitOnly
    },
    {
      ...getContentMetricsNavLink(isZalandoOutfitOnly),
      isAvailable: hasContent && !isZalandoOutfitOnly
    },
    {
      ...getSentimentAnalysisNavLink(isZalandoOutfitOnly),
      isAvailable: hasContent && !isZalandoOutfitOnly
    },
    {
      ...getDownloadNavLink(isZalandoOutfitOnly),
      isAvailable: hasContent && metaInfo.hasDefinedAttribution
    }
  ];
}
